// src/pages/.../NewsResponsablesCard.jsx
import React, { useState, useMemo } from "react";
import { FaEdit, FaTrashAlt } from "react-icons/fa";

const NewsResponsablesCard = ({ news, canModify, onEdit, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const imageUrl = news.image_url || "/img/default_image.jpg";

  const createdAt = news.created_at ? new Date(news.created_at) : null;

  // Longueur "texte brut" approximative pour décider d'afficher le bouton "Lire plus"
  const plainTextLength = useMemo(() => {
    const html = news.content || "";
    const withoutTags = html.replace(/<[^>]+>/g, "");
    return withoutTags.length;
  }, [news.content]);

  const shouldShowToggle = plainTextLength > 400;

  const borderClass = news.is_active
    ? "border-slate-200 dark:border-slate-700"
    : "border-amber-300 dark:border-amber-400";

  return (
    <article
      className={`group flex flex-col rounded-xl overflow-hidden bg-white/90 dark:bg-slate-900/90 border ${borderClass} shadow-sm hover:shadow-md transition-shadow`}
    >
      {/* Image en en-tête */}
      <div className="relative aspect-[16/9] w-full overflow-hidden">
        <img
          src={imageUrl}
          alt={news.title}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/0" />

        {/* Statut & date */}
        <div className="absolute top-2 left-2 flex flex-wrap gap-2">
          {!news.is_active && (
            <span className="inline-flex items-center rounded-full bg-amber-500/90 px-2 py-1 text-[0.65rem] font-semibold uppercase tracking-wide text-white">
              Non publiée (brouillon)
            </span>
          )}
        </div>

        {createdAt && (
          <div className="absolute bottom-2 right-2">
            <span className="inline-flex items-center rounded-full bg-black/70 px-2 py-1 text-[0.65rem] font-medium text-white">
              Publiée le{" "}
              {createdAt.toLocaleDateString("fr-FR", {
                year: "numeric",
                month: "short",
                day: "2-digit",
              })}
            </span>
          </div>
        )}
      </div>

      {/* Contenu */}
      <div className="flex flex-col gap-2 px-3 py-3 sm:px-4 sm:py-4 text-sm text-slate-700 dark:text-slate-200">
        {/* Titre */}
        <h3 className="text-base sm:text-lg font-bold uppercase font-montserrat text-slate-900 dark:text-slate-50">
          {news.title}
        </h3>

        {/* Auteur */}
        {news.author && (
          <p className="text-[0.75rem] sm:text-xs text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 rounded-md px-2 py-1">
            Message diffusé par{" "}
            <span className="font-semibold">
              {news.author.prenom} {news.author.nom}
            </span>
          </p>
        )}

        {/* Contenu long avec Lire plus / Lire moins */}
        <div className="mt-2">
          <div
            className={`relative text-xs sm:text-sm text-justify text-slate-700 dark:text-slate-200 ${
              !isExpanded ? "max-h-48 overflow-hidden" : ""
            }`}
          >
            <div dangerouslySetInnerHTML={{ __html: news.content }} />
            {!isExpanded && shouldShowToggle && (
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white dark:from-slate-900 to-transparent" />
            )}
          </div>

          {shouldShowToggle && (
            <button
              type="button"
              onClick={() => setIsExpanded((v) => !v)}
              className="mt-2 text-xs font-semibold uppercase tracking-wide text-primary hover:text-primary/80"
            >
              {isExpanded ? "Réduire le texte" : "Lire la suite"}
            </button>
          )}
        </div>

        {/* Actions bas de carte */}
        {canModify && (
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex items-center justify-center rounded-full bg-blue-500 hover:bg-blue-600 text-white text-xs px-3 py-1.5"
              title="Modifier l’actualité"
            >
              <FaEdit className="mr-1" />
              Modifier
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1.5"
              title="Supprimer l’actualité"
            >
              <FaTrashAlt className="mr-1" />
              Supprimer
            </button>
          </div>
        )}
      </div>
    </article>
  );
};

export default NewsResponsablesCard;
